import pageTitle from 'ember-page-title/helpers/page-title';
import Component from '@glimmer/component';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import { restartableTask, timeout } from 'ember-concurrency';
import { modifier } from 'ember-modifier';
import type PreRegisterService from '../services/pre-register';
import type RouterService from '@ember/routing/router';
import type SessionService from '../services/session';
import type Owner from '@ember/owner';
import { t } from 'ember-intl';
import TioFooter from 'tio-common/components/tio/footer';
import { navigation } from 'tio-common/utils/navigation';
import Button from 'tio-ui/components/button';
import {
  REMEMBER_ATTRIBUTE,
  forgetDestination,
  markRedirected,
  redirectedThisTab,
  rememberDestination,
  rememberedDestination,
  welcomeRedirect,
} from '../utils/welcome-destination';

export default class WelcomeRoute extends Component {
  @service declare preRegister: PreRegisterService;
  @service declare router: RouterService;
  @service declare session: SessionService;

  @tracked choosingAgain: boolean;

  constructor(owner: Owner, args: object) {
    super(owner, args);
    this.choosingAgain = redirectedThisTab(this.preRegister.subdomain);
  }

  get settings() {
    return this.preRegister.settings?.landingPage;
  }

  get title() {
    return this.settings?.title;
  }

  get content() {
    return this.settings?.html;
  }

  get buttonLabel() {
    return this.settings?.buttonText;
  }

  get buttonUrl() {
    return this.settings?.externalLoginUrl;
  }

  get remembersDestination() {
    return this.settings?.rememberDestination === true;
  }

  @cached
  get pendingDestination() {
    if (this.choosingAgain || !this.remembersDestination) return undefined;
    return rememberedDestination(this.preRegister.subdomain, this.content);
  }

  gotoLogin = async () => {
    this.session?.setTransientValue?.('landingPageViewed', true);
    this.router.transitionTo('login');
  };

  #focusChoices = false;

  chooseAgain = () => {
    this.followDestination.cancelAll();
    forgetDestination(this.preRegister.subdomain);
    this.#focusChoices = true;
    this.choosingAgain = true;
  };

  followDestination = restartableTask(async (href: string) => {
    await timeout(welcomeRedirect.delayMs);
    markRedirected(this.preRegister.subdomain);
    navigation.navigateTo(href);
  });

  scheduleRedirect = modifier((_element: HTMLElement, [href]: [string]) => {
    // A page restored from the back/forward cache would otherwise sit on "Taking you to…".
    const showChoices = (event: PageTransitionEvent) => {
      if (event.persisted) this.choosingAgain = true;
    };
    window.addEventListener('pageshow', showChoices);
    void this.followDestination.perform(href);
    return () => {
      window.removeEventListener('pageshow', showChoices);
      this.followDestination.cancelAll();
    };
  });

  // Delegated listener: the links live in admin-authored HTML, not in this template.
  rememberChoice = modifier((element: HTMLElement) => {
    if (this.#focusChoices) {
      this.#focusChoices = false;
      const target =
        element.querySelector<HTMLElement>(`a[${REMEMBER_ATTRIBUTE}]`) ??
        element.querySelector<HTMLElement>('a[href]');
      target?.focus();
    }

    const remember = (event: MouseEvent) => {
      const anchor = (event.target as Element | null)?.closest?.(`a[${REMEMBER_ATTRIBUTE}]`);
      const href = anchor?.getAttribute('href');
      if (this.remembersDestination && anchor && href && element.contains(anchor)) {
        rememberDestination(this.preRegister.subdomain, href);
      }
    };
    element.addEventListener('click', remember);
    return () => element.removeEventListener('click', remember);
  });

  <template>
    {{pageTitle (t "login.page_title")}}

    <div class="tio-col-flush w-screen h-screen overflow-y-auto">
      <div class="tio-col-flush relative lg:min-h-screen">
        <div class="absolute bottom-0 w-screen h-[20vh] lg:h-[30vh] bg-midnight" />

        <div class="lg:h-screen tio-col-flush lg:flex-row justify-around pt-8 lg:p-12 z-5">
          <div class="lg:w-[30vw]">
            <div class="tio-bg-surface rounded-lg pb-16">
              <h1 class="tio-text-2xl-semibold tio-fg-secondary text-center">
                {{this.title}}
              </h1>

              <div class="text-xl m-12">
                {{#if this.pendingDestination}}
                  <div
                    class="tio-col"
                    data-test-welcome-redirect
                    {{this.scheduleRedirect this.pendingDestination.href}}
                  >
                    <p role="status">
                      {{t "welcome.redirecting" destination=this.pendingDestination.label}}
                    </p>
                    <Button
                      @appearance="link"
                      @intent="primary"
                      @testHook="welcome__choose-again"
                      data-test-welcome-choose-again
                      {{on "click" this.chooseAgain}}
                    >
                      {{t "welcome.choose_different"}}
                    </Button>
                  </div>
                {{else}}
                  <div class="welcome-html" {{this.rememberChoice}}>
                    {{! template-lint-disable no-triple-curlies }}
                    {{{this.content}}}
                  </div>
                {{/if}}
              </div>

              {{#unless this.pendingDestination}}
                {{! If buttonUrl is defined in the landing page settings, it is
                used; otherwise gotoLogin is called when clicked }}
                {{#if this.buttonUrl}}
                  <a href={{this.buttonUrl}} class="cursor-pointer tio-fg-info underline">
                    {{this.buttonLabel}}
                  </a>
                {{else}}
                  <Button class="mx-auto" {{on "click" this.gotoLogin}}>
                    {{this.buttonLabel}}
                  </Button>
                {{/if}}
              {{/unless}}
            </div>
          </div>

          <div class="lg:w-[40vw] p-12 lg:p-0">
            <img
              class="lg:absolute lg:bottom-[20vh] lg:right-[10vw] lg:max-w-[40vw] max-h-[80vh]"
              src="/assets/images/meeting.svg"
              alt=""
            />
          </div>
        </div>
      </div>

      <div class="pb-20 mx-6 lg:mx-12">
        <TioFooter />
      </div>
    </div>
  </template>
}
